/**
 * Movie search page
 */

import React, { useState } from 'react';
import apiService, { MovieSearchResult } from '../services/api';
import MovieRatingDialog from '../components/MovieRatingDialog';
import styled from 'styled-components';

const SearchContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  color: var(--color-text);
  background: var(--color-surface);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const SearchButton = styled.button`
  background: var(--color-accent);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: var(--color-accent-hover);
  }

  &:disabled {
    background: var(--color-text-muted);
    cursor: not-allowed;
  }
`;

const ResultsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const MovieCard = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  overflow: hidden;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const MoviePoster = styled.div<{ $posterUrl?: string }>`
  height: 200px;
  background: ${props => props.$posterUrl
    ? `url(https://image.tmdb.org/t/p/w300${props.$posterUrl}) center/cover`
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
`;

const MovieInfo = styled.div`
  padding: 1.5rem;
`;

const MovieTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: var(--color-text);
`;

const MovieDetails = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const MovieOverview = styled.p`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RateButton = styled.button`
  background: var(--color-accent);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  width: 100%;

  &:hover {
    background: var(--color-accent-hover);
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
`;

const SearchPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MovieSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState<MovieSearchResult | null>(null);
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const response = await apiService.searchMovies({ query: query.trim() });
      setResults(response.results);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRateMovie = (movie: MovieSearchResult) => {
    setSelectedMovie(movie);
    setIsRatingDialogOpen(true);
  };

  const handleRatingSubmitted = () => {
    // Optionally refresh search results or show a success message
    console.log('Rating submitted successfully!');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).getFullYear();
  };

  return (
    <SearchContainer>
      <SearchForm onSubmit={handleSearch}>
        <SearchInput
          type="text"
          placeholder="Search for movies..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <SearchButton type="submit" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </SearchButton>
      </SearchForm>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading && <LoadingSpinner>Searching movies...</LoadingSpinner>}

      {!loading && hasSearched && results.length === 0 && (
        <NoResults>
          No movies found for "{query}". Try different keywords!
        </NoResults>
      )}

      {!loading && results.length > 0 && (
        <ResultsContainer>
          {results.map((movie) => (
            <MovieCard key={movie.tmdb_id}>
              <MoviePoster $posterUrl={movie.poster_path}>
                {!movie.poster_path && ''}
              </MoviePoster>
              <MovieInfo>
                <MovieTitle>{movie.title}</MovieTitle>
                <MovieDetails>
                  {formatDate(movie.release_date)} | ⭐ {movie.vote_average?.toFixed(1)}
                </MovieDetails>
                <MovieOverview>
                  {movie.overview || 'No description available.'}
                </MovieOverview>
                <RateButton onClick={() => handleRateMovie(movie)}>
                  Rate This Movie
                </RateButton>
              </MovieInfo>
            </MovieCard>
          ))}
        </ResultsContainer>
      )}

      {selectedMovie && (
        <MovieRatingDialog
          movie={selectedMovie}
          isOpen={isRatingDialogOpen}
          onClose={() => {
            setIsRatingDialogOpen(false);
            setSelectedMovie(null);
          }}
          onRatingSubmitted={handleRatingSubmitted}
        />
      )}
    </SearchContainer>
  );
};

export default SearchPage;